import React, { useState } from 'react';
import axios from 'axios';
import { BookOpen, Plus, Eye, EyeOff, Edit2, Trash2 } from 'lucide-react';
import { API, getToken } from './shared';

const BlogTab = ({ blogPosts, onRefresh }) => {
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm, setBlogForm] = useState({ title: '', content: '', excerpt: '', category: 'buying_guide', cover_image: '', tags: '' });

  const handleSave = async () => {
    try {
      const payload = {
        title: blogForm.title, content: blogForm.content, excerpt: blogForm.excerpt,
        category: blogForm.category, cover_image: blogForm.cover_image || null,
        tags: blogForm.tags ? blogForm.tags.split(',').map(t => t.trim()).filter(Boolean) : []
      };
      if (editingPost) {
        await axios.put(`${API}/admin/blog/${editingPost.id}`, payload, { headers: { Authorization: `Bearer ${getToken()}` } });
      } else {
        await axios.post(`${API}/admin/blog`, payload, { headers: { Authorization: `Bearer ${getToken()}` } });
      }
      setShowBlogForm(false);
      onRefresh();
    } catch (error) {
      console.error('Error saving blog post:', error);
      alert('Failed to save post. Please check all fields.');
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      await axios.put(`${API}/admin/blog/${post.id}`, { published: !post.published }, { headers: { Authorization: `Bearer ${getToken()}` } });
      onRefresh();
    } catch (error) { console.error('Error toggling publish:', error); }
  };

  const handleDeletePost = async (post) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await axios.delete(`${API}/admin/blog/${post.id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      onRefresh();
    } catch (error) { console.error('Error deleting post:', error); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-semibold">Blog Posts</h2>
        <button
          onClick={() => { setEditingPost(null); setBlogForm({ title: '', content: '', excerpt: '', category: 'buying_guide', cover_image: '', tags: '' }); setShowBlogForm(true); }}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20"
          data-testid="create-blog-btn"
        >
          <Plus className="w-5 h-5" /> New Post
        </button>
      </div>

      {showBlogForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowBlogForm(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8" onClick={e => e.stopPropagation()} data-testid="blog-form-modal">
            <h3 className="text-2xl font-bold mb-6">{editingPost ? 'Edit Post' : 'Create New Post'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input type="text" value={blogForm.title} onChange={e => setBlogForm({ ...blogForm, title: e.target.value })} className="w-full h-11 rounded-lg border border-slate-200 px-4 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500" placeholder="Article title" data-testid="blog-input-title" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                <select value={blogForm.category} onChange={e => setBlogForm({ ...blogForm, category: e.target.value })} className="w-full h-11 rounded-lg border border-slate-200 px-4 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500" data-testid="blog-input-category">
                  <option value="buying_guide">Buying Guide</option>
                  <option value="selling_guide">Selling Guide</option>
                  <option value="renting_guide">Renting Guide</option>
                  <option value="market_trends">Market Trends</option>
                  <option value="city_spotlight">City Spotlight</option>
                  <option value="home_loans">Home Loans</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Excerpt (short summary)</label>
                <textarea value={blogForm.excerpt} onChange={e => setBlogForm({ ...blogForm, excerpt: e.target.value })} rows={2} className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 resize-none" placeholder="Brief summary for the blog card..." data-testid="blog-input-excerpt" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Content</label>
                <textarea value={blogForm.content} onChange={e => setBlogForm({ ...blogForm, content: e.target.value })} rows={10} className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 resize-y" placeholder="Write your article content here..." data-testid="blog-input-content" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Cover Image URL (optional)</label>
                <input type="text" value={blogForm.cover_image} onChange={e => setBlogForm({ ...blogForm, cover_image: e.target.value })} className="w-full h-11 rounded-lg border border-slate-200 px-4 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500" placeholder="https://..." data-testid="blog-input-cover" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tags (comma separated, optional)</label>
                <input type="text" value={blogForm.tags} onChange={e => setBlogForm({ ...blogForm, tags: e.target.value })} className="w-full h-11 rounded-lg border border-slate-200 px-4 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500" placeholder="kerala, real estate, buying" data-testid="blog-input-tags" />
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={handleSave} disabled={!blogForm.title || !blogForm.content || !blogForm.excerpt} className="flex-1 h-11 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-testid="blog-save-btn">
                  {editingPost ? 'Update Post' : 'Publish Post'}
                </button>
                <button onClick={() => setShowBlogForm(false)} className="h-11 px-6 rounded-lg bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all" data-testid="blog-cancel-btn">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {blogPosts.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No Blog Posts Yet</h3>
          <p className="text-slate-500">Create your first blog post to get started</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-blog-list">
          {blogPosts.map(post => (
            <div key={post.id} className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={`admin-blog-${post.id}`}>
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-36 h-24 bg-slate-100 rounded-lg overflow-hidden">
                  {post.cover_image ? (
                    <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-900"><BookOpen className="w-8 h-8 text-white/30" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-1">
                    <h3 className="text-lg font-semibold text-slate-900 truncate">{post.title}</h3>
                    <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full flex-shrink-0 ${post.published ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                      {post.published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 truncate mb-2">{post.excerpt}</p>
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span className="capitalize">{post.category?.replace('_', ' ')}</span>
                    <span>·</span>
                    <span>{post.author}</span>
                    <span>·</span>
                    <span>{new Date(post.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => handleTogglePublish(post)} className="h-9 px-4 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 transition-all flex items-center gap-1.5" data-testid={`blog-toggle-${post.id}`}>
                    {post.published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    {post.published ? 'Unpublish' : 'Publish'}
                  </button>
                  <button onClick={() => { setEditingPost(post); setBlogForm({ title: post.title, content: post.content, excerpt: post.excerpt, category: post.category, cover_image: post.cover_image || '', tags: post.tags?.join(', ') || '' }); setShowBlogForm(true); }} className="h-9 px-4 rounded-lg bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 transition-all flex items-center gap-1.5" data-testid={`blog-edit-${post.id}`}>
                    <Edit2 className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => handleDeletePost(post)} className="h-9 px-4 rounded-lg bg-red-50 text-red-600 text-sm font-medium hover:bg-red-100 transition-all flex items-center gap-1.5" data-testid={`blog-delete-${post.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogTab;
